import { Box, Text } from '@factorialco/deprecated-design-system'
import { Spinner } from '@factorialco/f0-react/dist/experimental'

import i18n from 'modules/core/lib/i18n'

import { formatPrice } from 'modules/billing/lib/f25Prices'
import useSelfUpsellPrices from 'modules/upsell/hooks/useSelfUpsellPrices'
import type Plan from 'modules/upsell/lib/selfUpsell/plans/Plan'
import { useSelfUpsellContext } from 'modules/upsell/store/SelfUpsellProvider'

type ItemInfoProps = {
  title: string
  description: string
  price: string
}

const ItemInfo = ({ title, description, price }: ItemInfoProps) => {
  return (
    <Box flexDirection="row" justifyContent="spaceBetween" gap="s8">
      <Box gap="s2">
        <Text.Normal size="200" color="grey1000">
          {title}
        </Text.Normal>
        <Text.Normal size="100" color="grey700">
          {description}
        </Text.Normal>
      </Box>
      <Text.Normal size="200" color="grey1000">
        {price}
      </Text.Normal>
    </Box>
  )
}

type PaymentDetailProps = {
  addon: Plan
  crossellingAddon?: Plan | undefined
  billingDate: string
  discounts: { discountPercentage: number; amount: number; discountType: string }[] | undefined
  loadingPrice: boolean
}

const PaymentDetail = ({
  addon,
  crossellingAddon,
  billingDate,
  discounts,
  loadingPrice,
}: PaymentDetailProps) => {
  const {
    configPlan,
    seats,
    currentSeats,
    currency,
    isYearly,
    isExpenses,
    discount: manualDiscount,
    period,
    computeCrossSellingTotalPrice,
  } = useSelfUpsellContext()
  const {
    perSeatPrice,
    feePrice,
    stairstepPrice,
    tieredPrice,
    annualFeePrice,
    recalculatedDiscounts,
    formattedTotalPrice,
  } = useSelfUpsellPrices({
    addon,
    discounts,
  })
  const includedSeatsPerModule = tieredPrice?.tiers?.[0]?.endingUnit
  const yearlyText = isYearly()
    ? `x ${i18n.t('self_upsell.second_step.months', { months: 12 })}`
    : ''
  const descriptionText = () => {
    if (isExpenses()) {
      return [
        `${formatPrice(feePrice || 0, currency)} ${i18n.t(
          'pricing_plans.fixed_fee'
        )} ${yearlyText},`,

        i18n.t('self_upsell.first_step.expenses_seats_included', {
          seats: includedSeatsPerModule,
        }),
      ]
        .filter(Boolean)
        .join(' ')
    } else {
      return [
        perSeatPrice
          ? `${formatPrice(perSeatPrice, currency)}/mo x ${i18n.t('self_upsell.common.seats', {
              seats: currentSeats,
            })} ${yearlyText}`
          : null,
        feePrice
          ? `${formatPrice(feePrice, currency)} ${i18n.t('pricing_plans.fixed_fee')} ${yearlyText}`
          : null,
        stairstepPrice
          ? `${formatPrice(stairstepPrice, currency)} ${i18n.t(
              'pricing_plans.fixed_fee'
            )} ${yearlyText}`
          : null,
      ]
        .filter(Boolean)
        .join(' ')
    }
  }

  const itemInfo = () => {
    const variant = addon.getSpecificVariant(configPlan.variant || 'Business')
    const priceSeats = isExpenses() ? seats : currentSeats
    return (
      <ItemInfo
        key={addon.title}
        title={addon.title + ' ' + variant?.type}
        description={descriptionText()}
        price={formatPrice(variant?.pricing.getTotalPrice(period, priceSeats) || 0, currency)}
      />
    )
  }

  if (!configPlan.variant) return null
  return (
    <Box
      border={{
        all: {
          width: 's1',
          style: 'solid',
          color: 'grey300',
        },
      }}
      borderRadius={{ all: 'abs016' }}
      background="grey100"
      padding="s8"
      gap="s8"
    >
      <Box
        borderRadius={{ all: 'abs012' }}
        background="grey000White"
        boxShadow="s50"
        style={{ overflowY: 'auto', overflowX: 'hidden', maxHeight: '240px' }}
      >
        <Box gap="s12" padding="s16">
          {tieredPrice?.selectedPrice && feePrice ? (
            <>
              <ItemInfo
                key={addon.title}
                title={addon.title}
                description={descriptionText()}
                price={formatPrice(annualFeePrice || 0, currency)}
              />
              <ItemInfo
                key={addon.title}
                title={i18n.t('self_upsell.second_step.tiered_seats_title', {
                  addon: addon.title,
                })}
                description={i18n.t('self_upsell.second_step.tiered_seats', {
                  seats: seats - (tieredPrice.tiers?.[0]?.endingUnit || 0),
                })}
                price={formatPrice(tieredPrice.selectedPrice, currency)}
              />
            </>
          ) : (
            itemInfo()
          )}

          {crossellingAddon &&
            configPlan.addon?.includes(crossellingAddon.pricing.getPlanId(period) || '') && (
              <ItemInfo
                key={crossellingAddon.title}
                title={crossellingAddon.title}
                description={`${formatPrice(
                  crossellingAddon.pricing.getSeatsPrice(period) || 0,
                  currency
                )}/mo x ${i18n.t('self_upsell.common.seats', {
                  seats: currentSeats,
                })} ${yearlyText}`}
                price={formatPrice(computeCrossSellingTotalPrice(), currency)}
              />
            )}

          {recalculatedDiscounts.length > 0 && (
            <>
              {recalculatedDiscounts.map((discount, index) => (
                <ItemInfo
                  key={index}
                  title={i18n.t('self_upsell.second_step.discount', {
                    discount: discount.discountPercentage,
                  })}
                  description={
                    discount.discountType === 'item_level_discount'
                      ? i18n.t('self_upsell.second_step.manual_discount_description', {
                          period:
                            discount.discountPercentage === 100 ? 1 : manualDiscount?.period || 1,
                          periodUnit:
                            discount.discountPercentage === 100
                              ? i18n.t('self_upsell.second_step.discount_periods.year')
                              : manualDiscount?.periodUnit,
                        })
                      : i18n.t('self_upsell.second_step.discount_description')
                  }
                  price={`-${formatPrice(discount.amount, currency)}`}
                />
              ))}
            </>
          )}
        </Box>
        <Box
          border={{
            top: {
              width: 's1',
              style: 'dashed',
              color: 'grey200',
            },
          }}
          flexDirection="row"
          justifyContent="spaceBetween"
          gap="s8"
          padding="s16"
        >
          <Text.Normal size="200" weight="semibold" color="grey1000">
            {i18n.t('self_upsell.common.total')}
          </Text.Normal>
          <Text.Normal size="200" weight="semibold" color="grey1000">
            {loadingPrice ? <Spinner size="small" /> : formattedTotalPrice}
          </Text.Normal>
        </Box>
      </Box>

      <Box paddingX="s12" paddingY="s8">
        <Text.Normal size="200" color="grey800">
          {i18n.t('self_upsell.second_step.description_1')}{' '}
          <strong style={{ fontWeight: 500 }}>{formattedTotalPrice}</strong>{' '}
          {i18n.t('self_upsell.second_step.description_2')}{' '}
          <strong style={{ fontWeight: 500 }}>{billingDate}</strong>.{' '}
          {i18n.t('self_upsell.second_step.description_3')}
        </Text.Normal>
      </Box>
    </Box>
  )
}

export default PaymentDetail
